import { Suspense, lazy, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import { QuoteFormProvider } from "./components/QuoteFormContext";
import PremiumLoader from "./components/PremiumLoader";
import VisitPopup from "./components/VisitPopup";

// The landing page stays in the initial chunk so its LCP is never gated on a
// second request. Every other route is split out — they were previously all
// bundled into the first payload even though a visitor loads one at a time.
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const About = lazy(() => import("./pages/About"));
const Locations = lazy(() => import("./pages/Locations"));
const LocationDetail = lazy(() => import("./pages/LocationDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  const [loaderDone, setLoaderDone] = useState(false);
  return (
  /* The Toaster, Sonner and TooltipProvider wrappers the template mounted here
     have been removed. Nothing in the site ever calls toast() or renders a
     tooltip — grep finds zero usages outside the shadcn files themselves — so
     they were shipping sonner, @radix-ui/react-toast and @floating-ui/core in
     the initial chunk and doing mount work on every page load for markup that
     never appears. Re-add whichever is needed if a toast or tooltip is ever
     introduced. */
  <QueryClientProvider client={queryClient}>
    <QuoteFormProvider>
      <PremiumLoader onDone={() => setLoaderDone(true)} />
      <VisitPopup ready={loaderDone} />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/locations/:slug" element={<LocationDetail />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QuoteFormProvider>
  </QueryClientProvider>
  );
};

export default App;
